import { FormEngineInputElementProps } from '@form-engine/element-input/components/input-element';
import { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { apiName, defaultCategory } from './attachments-element';
import { useFormEngineElement } from '@form-engine/core-application/hooks/use-element';
import {
    Badge,
    Dropdown,
    FloatingLabel,
    FormControl,
    FormGroup,
    FormLabel,
    ProgressBar,
    Stack,
} from 'react-bootstrap';
import { useFormElementSession } from '@form-engine/core-application/hooks/use-formsession';
import { UploadState } from '../utils/upload-handling';
import classNames from '@form-engine/core-util/class-names';
import { useElementSessionUploads } from '../hooks/use-element-session-uploads';
import FormEngine from '@form-engine/core-application';
import { useFormEngineElementDisabled } from '@form-engine/core-application/hooks/use-element-disabled';
import { useFormEngineElementHidden } from '@form-engine/core-application/hooks/use-element-hidden';
import formatFileSize from '@form-engine/core-util/format-file-size';

function UploadInputAddButton({
    onAdd,
    categories,
    disabled,
}: {
    onAdd?: (category?: string, accept?: string, maxSize?: number) => void;
    categories?: {
        bsoCategory?: string;
        accept?: string;
        validationMaxAttachmentSize?: string;
    }[];
    disabled?: boolean;
}) {
    const [show, setShow] = useState(false);
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const i18n = FormEngine.i18n.prefix('attachment.');

    useEffect(() => {
        let mounted = true;

        const toggleOff = (e: MouseEvent) => {
            if (
                !mounted ||
                !toggleRef.current ||
                e.composedPath().includes(toggleRef.current)
            ) {
                return;
            }
            setShow(false);
        };
        window.addEventListener('click', toggleOff);

        return () => {
            mounted = false;
            window.removeEventListener('click', toggleOff);
        };
    }, [setShow]);

    if (!categories?.length) {
        return null;
    }

    if (categories.length === 1) {
        return (
            <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                    !disabled &&
                        onAdd?.(
                            categories[0].bsoCategory,
                            categories[0].accept,
                            Number(
                                categories[0].validationMaxAttachmentSize ?? 0
                            )
                        );
                }}
                disabled={disabled}
            >
                {['Other', 'Unclassified', ''].includes(
                    String(categories[0].bsoCategory)
                )
                    ? i18n.c(`uploadDropdown`)
                    : i18n.c(`uploadButton`, {
                          category: i18n.c(
                              `bsoCategory.${categories[0].bsoCategory || defaultCategory}`
                          ),
                      })}
            </Button>
        );
    }

    return (
        <Dropdown show={show}>
            <Dropdown.Toggle
                size="sm"
                variant="secondary"
                disabled={disabled}
                onClick={() => !disabled && setShow(true)}
                ref={toggleRef}
            >
                {i18n.c(`uploadDropdown`)}
            </Dropdown.Toggle>

            <Dropdown.Menu>
                {categories.map((category, key) => (
                    <Dropdown.Item
                        key={key}
                        onClick={() => {
                            !disabled &&
                                onAdd?.(
                                    category.bsoCategory,
                                    category.accept,
                                    Number(
                                        category.validationMaxAttachmentSize ??
                                            0
                                    )
                                );
                        }}
                    >
                        {i18n.c(
                            `bsoCategory.${category.bsoCategory || defaultCategory}`
                        )}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}

function UploadInputListElement({
    id,
    file,
    bsoCategory,
    loadState,
    onRemove,
    disabled = false,
    bordered = false,
}: UploadState & {
    disabled?: boolean;
    onRemove?: (attachmentId: string) => void;
    bordered?: boolean;
}) {
    const nonInteractive = disabled || loadState !== 'uploaded';
    const i18n = FormEngine.i18n.prefix('attachment.');

    return (
        <Stack
            direction="horizontal"
            gap={3}
            className={classNames(
                bordered && 'border-top',
                nonInteractive && 'opacity-25 pe-none',
                'me-2'
            )}
        >
            <div className="p-2 text-sm">
                <h5>{file.name}</h5>
                <div>
                    <Badge bg="secondary">{formatFileSize(file.size)}</Badge>
                    <Badge bg="secondary">
                        {i18n.c(
                            `bsoCategory.${bsoCategory || defaultCategory}`
                        )}
                    </Badge>
                </div>
            </div>
            <div className="p-2 ms-auto">
                <Button
                    size="sm"
                    variant="light"
                    className={'p-0'}
                    style={{ width: '2rem', height: '2rem' }}
                    disabled={nonInteractive}
                    onClick={() => !nonInteractive && onRemove?.(id)}
                >
                    <span>&times;</span>
                </Button>
            </div>
        </Stack>
    );
}

export default function BsoAttachmentsUploadInputList({
    formGroupProps = { className: 'fe-attachment-input' },
}: FormEngineInputElementProps) {
    const { state: element } = useFormEngineElement();
    if (!element || element.type !== apiName)
        throw new Error('tried to render input with invalid definition');

    const [session] = useFormElementSession();

    const { addUpload, removeUpload, uploads, categories } =
        useElementSessionUploads();

    const disabled = useFormEngineElementDisabled();

    const nonInteractive = Boolean(session.loading || disabled);
    const hidden = useFormEngineElementHidden();

    if (hidden) {
        return null;
    }

    const loader = session.loading !== undefined &&
        session.loading !== false && (
            <ProgressBar
                className="mt-1"
                style={{ height: '5px' }}
                animated
                striped
                now={
                    typeof session.loading === 'number' ? session.loading : 100
                }
            />
        );

    const errorText = session.error || session.transientError;

    const error = !loader &&
        session.isTouched &&
        element.showError &&
        errorText && (
            <FormControl.Feedback type="invalid" tooltip={false}>
                {String(errorText)}
            </FormControl.Feedback>
        );

    const control = (
        <div
            className={classNames(
                'form-control p-2',
                !loader && error && 'is-invalid',
                !loader && session.isTouched && !session.error && 'is-valid'
            )}
        >
            <UploadInputAddButton
                categories={categories}
                onAdd={addUpload}
                disabled={nonInteractive}
            />
            <Stack
                className={classNames(
                    uploads?.length && categories.length && 'mt-2',
                    'me-4'
                )}
            >
                {uploads?.map((upload, index) => {
                    return (
                        <UploadInputListElement
                            {...upload}
                            key={upload.id}
                            bordered={index !== 0 || Boolean(categories.length)}
                            disabled={nonInteractive}
                            onRemove={removeUpload}
                        />
                    );
                })}
            </Stack>
        </div>
    );

    return (
        <FormGroup
            {...(formGroupProps ?? {})}
            className={classNames(
                'fe-attachment-list',
                'user-select-none',
                formGroupProps?.className
            )}
        >
            <>
                {element.label && element.floatingLabel ? (
                    <FloatingLabel label={element.label} controlId={element.id}>
                        <>
                            <control.type {...control.props} id={undefined} />
                            {error}
                        </>
                    </FloatingLabel>
                ) : (
                    <>
                        {element.label && (
                            <FormLabel htmlFor={element.id}>
                                {element.label}
                            </FormLabel>
                        )}
                        {control}
                        {error}
                    </>
                )}
                {loader}
            </>
        </FormGroup>
    );
}
